import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import { Card, CardContent } from '../../../../components/ui/Card';
import { levelLabel, statusLabel } from '../utils';
import type { Goal, ExpandedSections } from '../types';

interface MobileGoalTreeProps {
  goals?: Goal[];
  onGoalClick?: (goal: Goal) => void;
  expandedSections?: ExpandedSections;
  onToggleSection?: (section: keyof ExpandedSections, isExpanded: boolean) => void;
}

const MobileGoalTree: React.FC<MobileGoalTreeProps> = ({
  goals = [],
  onGoalClick = () => {},
  expandedSections = { company: true, team: true, individual: true },
  onToggleSection = () => {},
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const getStatusIcon = (status: Goal['status']) => {
    switch (status) {
      case 'completed': return { name: 'CheckCircle', className: 'text-success' };
      case 'on-track': return { name: 'Flag', className: 'text-primary' };
      case 'at-risk': return { name: 'AlertTriangle', className: 'text-warning' };
      case 'overdue': return { name: 'AlertCircle', className: 'text-error' };
      default: return { name: 'Flag', className: 'text-muted-foreground' };
    }
  };

  const getStatusColor = (status: Goal['status']): string => {
    switch (status) {
      case 'completed': return 'border-success text-success';
      case 'on-track': return 'border-primary text-primary';
      case 'at-risk': return 'border-warning text-warning';
      case 'overdue': return 'border-error text-error';
      default: return 'border-border text-muted-foreground';
    }
  };

  const getLevelIcon = (level: Goal['level']) => {
    switch (level) {
      case 'company': return 'Building2';
      case 'team': return 'Users';
      case 'individual': return 'User';
      default: return 'Flag';
    }
  };

  const getLevelColor = (level: Goal['level']) => {
    switch (level) {
      case 'company': return 'bg-primary';
      case 'team': return 'bg-warning';
      case 'individual': return 'bg-muted-foreground';
      default: return 'bg-muted-foreground';
    }
  };

  const filteredGoals = goals.filter(goal =>
    goal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    goal.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedGoals = filteredGoals.reduce((acc, goal) => {
    if (!acc[goal.level]) {
      acc[goal.level] = [];
    }
    acc[goal.level].push(goal);
    return acc;
  }, {} as Record<Goal['level'], Goal[]>);

  const levelOrder: Goal['level'][] = ['company', 'team', 'individual'];
  const levelLabels = {
    company: t('Company Objectives'),
    team: t('Team Goals'),
    individual: t('Individual Goals'),
  };

  return (
    <div className="flex flex-col gap-2 min-w-0">
      {/* Search */}
      <div className="relative">
        <AppIcon name="Search" size={16} className="absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder={t('Search goals...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full ps-8 pe-3 py-2 min-h-[44px] text-sm border border-border rounded-lg bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        />
      </div>

      {/* Goal Sections */}
      {levelOrder.map((level) => {
        const levelGoals = groupedGoals[level] || [];
        if (levelGoals.length === 0) return null;

        return (
          <div key={level} className="border border-border rounded-lg mb-1 overflow-hidden">
            {/* Accordion Header */}
            <button
              className="flex items-center justify-between w-full p-3 text-left hover:bg-muted transition-colors"
              onClick={() => onToggleSection(level, !expandedSections[level])}
            >
              <div className="flex items-center gap-2 flex-1">
                <div className={`w-8 h-8 rounded-full ${getLevelColor(level)} flex items-center justify-center`}>
                  <AppIcon name={getLevelIcon(level)} size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-base font-medium">
                    {levelLabels[level]}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('{{count}} goal', { count: levelGoals.length })}
                  </p>
                </div>
              </div>
              <AppIcon
                name="ChevronDown"
                size={18}
                className={`text-muted-foreground transition-transform duration-200 ${expandedSections[level] ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Accordion Content */}
            {expandedSections[level] && (
              <div>
                {levelGoals.map((goal, index) => (
                  <button
                    key={goal.id}
                    type="button"
                    aria-label={t('goals.map.nodeLabel', {
                      title: goal.title,
                      level: levelLabel(t, goal.level),
                      status: statusLabel(t, goal.status),
                      progress: goal.progress,
                    })}
                    className={`flex items-start w-full text-start py-3 px-3 cursor-pointer hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                      index < levelGoals.length - 1 ? 'border-b border-border' : ''
                    }`}
                    onClick={() => onGoalClick(goal)}
                  >
                    {/* Status Icon */}
                    <div className="w-10 min-w-[44px] flex-shrink-0 pt-0.5">
                      <AppIcon
                        name={getStatusIcon(goal.status).name}
                        size={18}
                        className={getStatusIcon(goal.status).className}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-medium break-words">
                        {goal.title}
                      </p>
                      {goal.description && (
                        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                          {goal.description}
                        </p>
                      )}

                      {/* Progress Bar */}
                      <div className="mt-2">
                        <div className="flex justify-between mb-0.5">
                          <span className="text-xs text-muted-foreground">{t('Progress')}</span>
                          <span className="text-xs font-medium">{goal.progress}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${goal.progress}%` }}
                          />
                        </div>
                      </div>

                      {/* Owner + Status */}
                      <div className="flex justify-between items-center gap-1 mt-2 min-w-0">
                        <span className="text-xs text-muted-foreground min-w-0 flex-1 truncate">
                          {goal.owner}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium uppercase border ${getStatusColor(goal.status)}`}>
                          {statusLabel(t, goal.status)}
                        </span>
                      </div>
                    </div>

                    {/* Chevron */}
                    <div className="flex-shrink-0 ms-2 pt-1">
                      <AppIcon name="ChevronRight" size={16} className="text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* No Results */}
      {filteredGoals.length === 0 && searchTerm && (
        <Card variant="elevated">
          <CardContent className="p-6 text-center">
            <AppIcon name="Search" size={48} className="text-muted-foreground mx-auto mb-2" />
            <h3 className="text-base font-medium mb-1">
              {t('No goals found')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('Try adjusting your search terms or clear the search to see all goals.')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MobileGoalTree;
